// src/mobile_pages/meeting/MeetingPlanShellMobile.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Calendar, {
  buildCounts,
  getKRHolidayMap,
} from "../../components/common/feature/Calendar";
import { format } from "date-fns";
import { meetingApi, getMyUserIdFromToken } from "../../api/meeting";
import api from "../../api/axios";
import MobileCreatePanel from "../../components/meeting/panels/MobileCreatePanel";
import DetailPanel from "../../components/meeting/panels/MobileDetailPanel";
import EditPanel from "../../components/meeting/panels/EditPanel";
import { createPortal } from "react-dom";
import SidePanel from "../../components/common/layout/SidePanel";

// ---------- 유틸 ----------
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toDateStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function normalizeStr(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}
function buildCompositeKey(title, iso, location) {
  return `${normalizeStr(title)}|${iso}|${normalizeStr(location)}`;
}
const isUrl = (v = "") => /^https?:\/\//i.test(v);

// 모바일 전용 참석자 선택 시트
// eslint-disable-next-line no-unused-vars
function MobileParticipantSheet({
  open,
  onClose,
  users,
  selectedIds,
  onChange,
}) {
  const [keyword, setKeyword] = useState("");
  const [localIds, setLocalIds] = useState(() =>
    (selectedIds || []).map((v) => Number(v))
  );

  useEffect(() => {
    setLocalIds((selectedIds || []).map((v) => Number(v)));
  }, [selectedIds, open]);

  const filtered = useMemo(() => {
    const q = keyword.trim();
    if (!q) return users || [];
    return (users || []).filter((u) => {
      const name = `${u.last_name || ""}${u.first_name || ""}`.trim();
      const position = String(u.position || "");
      const username = String(u.username || "");
      return (
        name.includes(q) ||
        position.includes(q) ||
        username.includes(q)
      );
    });
  }, [users, keyword]);

  const toggle = (rawId) => {
    const id = Number(rawId);
    setLocalIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleDone = () => {
    onChange?.(localIds); // 선택값 부모에 반영
    onClose?.(); // 시트 닫기
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md mx-auto rounded-t-3xl bg-white shadow-xl">
        <div className="px-5 pt-3 pb-4 flex flex-col gap-3">
          <div className="mx-auto h-1 w-10 rounded-full bg-slate-200" />

          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-slate-900">
              참석자 선택
            </div>
            <button
              type="button"
              className="text-sm font-medium text-indigo-600"
              onClick={handleDone}
            >
              완료
            </button>
          </div>

          <div>
            <input
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none"
              placeholder="이름 또는 직책으로 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>

          <div className="max-h-[60vh] -mx-1 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-400">
                검색 결과가 없습니다.
              </p>
            )}

            {filtered.map((u) => {
              const id = Number(u.id);
              const name =
                `${u.last_name || ""}${u.first_name || ""}`.trim() ||
                u.username ||
                "";
              const selected = localIds.includes(id);

              return (
                <button
                  key={id}
                  type="button"
                  className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-sm ${selected
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-slate-900 hover:bg-slate-50"
                    }`}
                  onClick={() => toggle(id)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{name}</span>
                    {u.position && (
                      <span className="text-xs text-slate-400">
                        {u.position}
                      </span>
                    )}
                  </div>
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded-full border ${selected
                      ? "border-indigo-500 bg-indigo-500"
                      : "border-slate-300 bg-white"
                      }`}
                  >
                    {selected && (
                      <span className="h-2 w-2 rounded-full bg-white" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function resolveLocationType(row) {
  const raw = row?.location_type;
  if (raw === "online" || raw === "offline_room" || raw === "offline_address") {
    return raw;
  }
  const loc = row?.location || "";
  if (!loc) return "offline_address";
  if (isUrl(loc)) return "online";
  return "offline_address";
}

function getLocationLabel(row) {
  const t = resolveLocationType(row);
  if (t === "online") return "온라인";
  if (t === "offline_room") return "오프라인(회의실)";
  return "오프라인(주소)";
}

// 시간 포맷 (없으면 "시간 미정")
// -> 카드 표시용
function formatTime(schedule) {
  if (!schedule) return "시간 미정";
  try {
    const d = new Date(schedule);
    return format(d, "HH:mm");
  } catch {
    return "시간 미정";
  }
}

// ---------- 페이지 셸 ----------
export default function MeetingPlanShellMobile() {
  // 라우팅 파라미터/도구
  const { id: projectId, dns: dnsParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 내 정보
  const myUserId = useMemo(() => Number(getMyUserIdFromToken() || 0), []);

  // 날짜 상태
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const dateStr = useMemo(() => toDateStr(selectedDate), [selectedDate]);

  // 하단 시트 오픈 상태
  const [sheetOpen, setSheetOpen] = useState(false); // 날짜 탭 시 true

  // 데이터 상태
  const [loadingAll, setLoadingAll] = useState(false);
  const [allEvents, setAllEvents] = useState([]);

  // 사이드패널 상태 (create / view / edit)
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState(null); // 'view' | 'create' | 'edit'
  const [activeItem, setActiveItem] = useState(null);
  const panelWidth = useMemo(() => {
    if (typeof window === "undefined") return 420;
    return Math.min(Math.max(window.innerWidth - 24, 360), 520);
  }, []);

  const openView = useCallback(
    (item) => {
      // 상세 패널 열기
      setActiveItem(item);
      setPanelMode("view");
      setPanelOpen(true);

      // 내가 참석 대상인데 아직 미응답이면 RSVP 같이 열기
      try {
        const mine = (item?.participants || []).find(
          (p) => Number(p.user_id) === myUserId
        );
        if (item?.is_required_for_me && mine && !mine.responded) {
          setRsvpOpen(true);
        } else {
          setRsvpOpen(false);
        }
      } catch {
        // 안전장치
      }
    },
    [myUserId]
  );

  const openCreate = () => {
    // 회의 등록 패널 열기
    setActiveItem(null);
    setPanelMode("create");
    setPanelOpen(true);
  };

  const openEdit = (item) => {
    // 회의 수정 패널 열기
    setActiveItem(item);
    setPanelMode("edit");
    setPanelOpen(true);
  };

  const closePanel = () => {
    // 모든 패널 + RSVP 닫기
    setPanelOpen(false);
    setPanelMode(null);
    setActiveItem(null);
    setRsvpOpen(false);
    navigate(`/project/${projectId}/meeting/plan`, { replace: true });
  };

  // RSVP 오버레이용 계산 (캘린더 영역 위치를 기준 anchor)
  const leftPanelRef = useRef(null);
  const [leftRect, setLeftRect] = useState(null);
  const updateLeftRect = useCallback(() => {
    if (!leftPanelRef.current) return;
    const r = leftPanelRef.current.getBoundingClientRect();
    setLeftRect({
      left: r.left + window.scrollX,
      top: r.top + window.scrollY,
      width: r.width,
      height: r.height,
    });
  }, []);
  useEffect(() => {
    updateLeftRect();
    const onResize = () => updateLeftRect();
    const onScroll = () => updateLeftRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, [updateLeftRect, panelOpen, selectedDate, allEvents]);

  // 디테일 패널 열림 중이면 최신 아이템으로 동기화
  useEffect(() => {
    if (!activeItem || panelMode !== "view") return;
    const fresh = allEvents.find((m) => m.id === activeItem.id);
    if (fresh) setActiveItem(fresh);
  }, [allEvents, activeItem, panelMode]);

  // 휴일 맵/카운트 (캘린더 하이라이트용)
  const countsByDate = useMemo(
    () => buildCounts(allEvents, (m) => m.schedule),
    [allEvents]
  );
  const [activeYear, setActiveYear] = useState(selectedDate.getFullYear());
  const [holidayMap, setHolidayMap] = useState({});
  const loadHolidays = useCallback(async (year) => {
    try {
      const map = await getKRHolidayMap(year);
      setHolidayMap((prev) => ({ ...prev, ...map }));
    } catch {
      // 휴일 API 에러는 조용히 무시
    }
  }, []);
  useEffect(() => {
    loadHolidays(activeYear);
  }, [activeYear, loadHolidays]);

  // 전체 목록 로딩
  const fetchAll = useCallback(
    async (options = { silent: false }) => {
      if (!options.silent) setLoadingAll(true);
      try {
        let url = "/meeting/meetings/";
        let params = {
          ordering: "schedule",
          ...(projectId ? { project: projectId } : {}),
          // 서버 구버전 대응: 삭제 문서 제외 요청
          include_deleted: 0,
        };
        const collected = [];
        while (url) {
          const { data } = await api.get(url, { params });
          params = undefined;
          if (Array.isArray(data.results)) {
            collected.push(...data.results);
            url = data.next
              ? data.next.replace(/^https?:\/\/[^/]+\/api/, "")
              : null;
          } else {
            url = null;
          }
        }
        const visible = collected.filter((m) => m?.is_deleted !== true);
        visible.sort((a, b) =>
          String(a.schedule).localeCompare(String(b.schedule))
        );
        setAllEvents(visible);
      } catch (e) {
        console.error(e);
        setAllEvents([]);
      } finally {
        if (!options.silent) setLoadingAll(false);
      }
    },
    [projectId]
  );
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // 쿼리파라미터(meeting, rsvp_reset)로 상세/RSVP 처리
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const resetRanRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const mid = Number(sp.get("meeting"));
    const wantReset = ["1", "true", "True"].includes(sp.get("rsvp_reset"));
    if (!mid || !Array.isArray(allEvents) || allEvents.length === 0) return;

    const target = allEvents.find((m) => Number(m.id) === mid);
    if (!target) return;

    openView(target);

    const mine = (target.participants || []).find(
      (p) => Number(p.user_id) === myUserId
    );

    if (wantReset && !resetRanRef.current) {
      (async () => {
        try {
          await meetingApi.rsvpReset(mid);
          await fetchAll({ silent: true });
          setRsvpOpen(true);
          navigate(`/project/${projectId}/meeting/plan?meeting=${mid}`, {
            replace: true,
          });
        } catch (e) {
          console.error(e);
        } finally {
          resetRanRef.current = true;
        }
      })();
      return;
    }

    if (mine && !mine.responded) setRsvpOpen(true);
    else setRsvpOpen(false);
  }, [location.search, allEvents, myUserId, navigate, projectId, fetchAll, openView]);

  // DNS 기반 진입 처리 (링크로 바로 들어온 경우)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const dnsFromQuery = sp.get("dns");
    const dns = dnsParam || dnsFromQuery;
    if (!dns) return;
    if (loadingAll) return;
    if (!allEvents.length) return;

    const target = allEvents.find((m) => m.dns === dns);
    if (!target) return;

    openView(target);

    if (dnsFromQuery) {
      navigate(`/project/${projectId}/meeting/plan?meeting=${target.id}`, {
        replace: true,
      });
    }
  }, [dnsParam, location.search, loadingAll, allEvents, navigate, projectId, openView]);

  // 선택한 날짜의 회의 목록 (정렬 포함)
  const list = useMemo(() => {
    const filtered = allEvents.filter(
      (m) => (m.schedule || "").slice(0, 10) === dateStr
    );
    return filtered.sort((a, b) => {
      const ua = a?.is_urgent ? 1 : 0;
      const ub = b?.is_urgent ? 1 : 0;
      if (ua !== ub) return ub - ua;
      return String(a.schedule || "").localeCompare(String(b.schedule || ""));
    });
  }, [allEvents, dateStr]);

  // 중복 체크용 CompositeKey 집합 (등록/수정 시 사용)
  const existingCompositeKeySet = useMemo(() => {
    try {
      return new Set(
        allEvents
          .map((e) => {
            try {
              const iso = new Date(e.schedule).toISOString();
              return buildCompositeKey(e.title, iso, e.location);
            } catch {
              return null;
            }
          })
          .filter(Boolean)
      );
    } catch {
      return new Set();
    }
  }, [allEvents]);

  // SidePanel title=null 처리로 헤더 제거 (내부 컴포넌트 헤더 사용)

  // 캘린더 날짜 변경 핸들러
  const handleDateChange = (nextDate) => {
    // 선택 날짜 변경 + 시트 오픈
    setSelectedDate(nextDate);
    setSheetOpen(true);
  };

  // 카드 클릭 핸들러
  const handleMeetingClick = (row) => {
    openView(row);
    // 상세가 사이드 패널로 뜨므로, 하단 시트는 자연스럽게 닫아줌
    setSheetOpen(false);
  };

  // ---------- 실제 화면 ----------
  return (
    // 전체 모바일 페이지 배경 + 패딩
    <div
      className="relative flex min-h-screen justify-center bg-gradient-to-b from-slate-100 to-slate-50 px-4 pt-6 pb-28"
    /* 기종별로 중앙 정렬을 안정적으로 유지 */
    >
      <div
        ref={leftPanelRef}
        className="w-full max-w-md mx-auto flex flex-col items-center" // 모바일 기준 메인 래퍼 중앙 정렬
      >
        {/* 상단 타이틀 영역 */}
        <div className="w-full mb-5 px-1">
          <p className="text-[11px] text-indigo-600 font-bold tracking-wide uppercase mb-1">회의/협업</p>
          <h1 className="text-[24px] font-bold leading-tight tracking-tight text-slate-900">
            회의 일정 관리
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-slate-500">
            달력에서 날짜를 탭하면 해당 날짜의 회의를 확인하고 등록할 수 있습니다.
          </p>
        </div>

        {loadingAll ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-slate-200 border-t-indigo-500 rounded-full animate-spin" />
            <p className="text-sm text-slate-400 mt-3">회의 데이터를 불러오는 중...</p>
          </div>
        ) : (
          <section
            ref={leftPanelRef}
            className="w-full rounded-[28px] border border-slate-100/80 bg-white px-4 sm:px-6 pt-6 pb-7 shadow-[0_8px_40px_rgba(15,23,42,0.12)]"
          >
            {/* 캘린더 컴포넌트 자체는 기존 로직 재사용 */}
            <div className="flex flex-col items-center w-full">
              <div className="w-full max-w-[400px]">
                <Calendar
                  value={selectedDate}
                  onChange={handleDateChange}
                  countsByDate={countsByDate}
                  holidaysByDate={holidayMap}
                  onMonthYearChange={setActiveYear}
                />
              </div>
            </div>
          </section>
        )}
      </div>

      {/* ─────────────────────────────────────
          하단 바텀 시트 (선택 날짜 · 회의 목록 · 회의 등록 버튼)
         ───────────────────────────────────── */}
      <div
        className={[
          "pointer-events-none fixed inset-0 z-[900] flex items-end justify-center transition-opacity duration-300",
          sheetOpen ? "opacity-100" : "opacity-0",
        ].join(" ")}
      >
        {/* 반투명 배경 (시트 열려 있을 때만 클릭 가능) */}
        <div
          className="absolute inset-0 bg-slate-900/25 backdrop-blur-[2px]"
          style={{ pointerEvents: sheetOpen ? "auto" : "none" }}
          onClick={() => setSheetOpen(false)} // 시트 바깥 터치 시 닫기
        />

        {/* 실제 시트 컨테이너 */}
        <div
          className={[
            "pointer-events-auto w-full max-w-md transform-gpu transition-transform duration-300 ease-out",
            sheetOpen ? "translate-y-0" : "translate-y-full",
          ].join(" ")}
        >
          <div className="mx-4 mb-0 overflow-hidden rounded-t-3xl border border-slate-100 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
            {/* 상단 그립 핸들 */}
            <div className="flex items-center justify-center py-3">
              <div className="h-1.5 w-10 rounded-full bg-slate-200" />
            </div>

            {/* 선택 날짜 + 회의 등록 버튼 */}
            <div className="flex items-center justify-between gap-3 px-5 pb-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                  선택한 날짜
                </p>
                <p className="mt-1 text-[15px] font-semibold text-slate-900">
                  {format(selectedDate, "yyyy년 MM월 dd일 (EEE)", {
                    locale: undefined,
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSheetOpen(false);
                  openCreate();
                }}
                className="shrink-0 rounded-full bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm active:scale-95"
              >
                회의 등록
              </button>
            </div>

            {/* Upcoming 라벨 */}
            <div className="px-5 pt-1 pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Upcoming
              </p>
            </div>

            {/* 회의 카드 목록 */}
            <div className="px-5 pb-4">
              {list.length === 0 ? (
                // 선택한 날짜에 회의가 없을 때 메시지
                <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                  선택한 날짜에 예정된 회의가 없습니다.
                </p>
              ) : (
                <div
                  className="space-y-2 overflow-y-auto"
                  style={{ maxHeight: "46vh" }} // 너무 길어지면 안쪽에서만 스크롤
                >
                  {list.map((row) => {
                    const timeLabel = formatTime(row.schedule);
                    const locLabel = getLocationLabel(row);
                    const isUrgent = !!row?.is_urgent;
                    const isMine = !!row?.is_required_for_me;

                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => handleMeetingClick(row)}
                        className="group flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left shadow-[0_10px_30px_rgba(15,23,42,0.08)] active:scale-[0.98] transition-transform"
                      >
                        {/* 시간 원형 뱃지 */}
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600/10 text-[11px] font-semibold text-indigo-700">
                          {timeLabel}
                        </div>

                        {/* 텍스트 영역 */}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[14px] font-semibold text-slate-900 group-hover:text-indigo-700">
                            {row.title}
                          </p>
                          <p className="mt-1 break-words text-[12px] text-slate-500">
                            {row.location ||
                              row.description ||
                              "세부 내용이 없습니다."}
                          </p>

                          <div className="mt-2 flex flex-wrap gap-1">
                            {isUrgent && (
                              <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                                긴급
                              </span>
                            )}

                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700">
                              {locLabel}
                            </span>

                            {isMine && (
                              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                내 참석 대상
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 사이드 패널 (등록/상세/수정) */}
      <SidePanel
        isOpen={panelOpen}
        onClose={closePanel}
        title={null}
        width={panelWidth}
      >
        {panelMode === "create" && (
          <MobileCreatePanel
            projectId={projectId}
            selectedDate={selectedDate}
            existingCompositeKeySet={existingCompositeKeySet}
            onSaved={(created) => {
              // 저장 후 새 회의 상세로 이동 + 목록 갱신
              navigate(
                `/project/${projectId}/meeting/plan?meeting=${created.id}`,
                { replace: true }
              );
              fetchAll({ silent: true });
            }}
            onClose={closePanel}
          />
        )}
        {panelMode === "view" && activeItem && (
          <DetailPanel
            item={activeItem}
            projectId={projectId}
            myUserId={myUserId}
            onReload={() => fetchAll({ silent: true })}
            onEdit={() => openEdit(activeItem)}
            onDeleted={() => {
              fetchAll({ silent: true });
              closePanel();
            }}
            onClose={closePanel}
          />
        )}
        {panelMode === "edit" && activeItem && (
          <EditPanel
            item={activeItem}
            projectId={projectId}
            existingCompositeKeySet={existingCompositeKeySet}
            onSaved={(updated) => {
              // 저장 후 수정된 회의 상세로 이동 + 목록 갱신
              navigate(
                `/project/${projectId}/meeting/plan?meeting=${updated?.id ?? activeItem?.id
                }`,
                { replace: true }
              );
              fetchAll({ silent: true });
            }}
            onClose={() => {
              // 취소 시 서버 상태로 동기화 후 원래 상세로 이동
              navigate(
                `/project/${projectId}/meeting/plan?meeting=${activeItem?.id}`,
                { replace: true }
              );
              fetchAll({ silent: true });
            }}
          />
        )}
      </SidePanel>

      {/* RSVP 오버레이 (캘린더 영역 기준) */}
      {panelMode === "view" &&
        rsvpOpen &&
        activeItem &&
        createPortal(
          <div
            className="fixed z-[1200]"
            style={{
              left: `${leftRect?.left ?? 0}px`,
              top: "22%",
              width: `${leftRect?.width ?? 0}px`,
              height: `${leftRect?.height ?? 0}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
            }}
            onClick={() => setRsvpOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-slate-300 bg-white p-5 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="mb-2 text-lg font-semibold">
                이 회의에 참여하시겠습니까?
              </h4>
              <p className="mb-5 text-sm text-slate-600">
                {activeItem?.title} ·{" "}
                {activeItem?.schedule?.slice(0, 16)?.replace("T", " ")}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  className="rounded-xl bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
                  onClick={async () => {
                    await meetingApi.rsvp(activeItem.id, true);
                    await fetchAll({ silent: true });
                    setRsvpOpen(false);
                  }}
                >
                  네
                </button>
                <button
                  className="rounded-xl border bg-white px-3 py-2 text-sm"
                  onClick={() => setRsvpOpen(false)}
                >
                  나중에 답할게요
                </button>
                <button
                  className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100"
                  onClick={async () => {
                    await meetingApi.rsvp(activeItem.id, false);
                    await fetchAll({ silent: true });
                    setRsvpOpen(false);
                  }}
                >
                  아니오
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
